import { useEffect, useMemo, useState } from 'react';
import {
  useLoaderData,
  useNavigation,
  useSearchParams,
} from 'react-router-dom';
import type { ContractPage } from '@/api/schemas';
import { ContractResultsTable } from '@/components/search/ContractResultsTable';
import {
  PAGE_SIZE_OPTIONS,
  ResultsPagination,
} from '@/components/search/ResultsPagination';
import { SearchForm } from '@/components/search/SearchForm';
import { EmptyState } from '@/components/ui/empty-state';
import { useClock } from '@/lib/clock';
import {
  type SortDirection,
  type SortKey,
  sortContracts,
} from '@/lib/contractSort';
import { pageCount, pageSlice } from '@/lib/pagination';
import type { SearchLoaderData } from '@/routes/loaders';

function ResultsSummary({
  page,
  query,
}: {
  page: ContractPage;
  query: string;
}) {
  const shown = page.contracts.length;

  return (
    <div className="mx-6 flex min-h-14 items-center rounded-md bg-panel-data px-2 py-3 text-[14px] leading-[18px] text-[#0A0A0A]">
      {shown < page.total ? (
        <span>
          Showing <span className="font-bold">{shown}</span> of{' '}
          <span className="font-bold">{page.total.toLocaleString()}</span>{' '}
          results for: <span className="font-bold">{query}</span>. Refine the
          search to narrow them down.
        </span>
      ) : (
        <span>
          <span className="font-bold">{page.total}</span>&nbsp;Results for:{' '}
          <span className="font-bold">{query}</span>
        </span>
      )}
    </div>
  );
}

function SearchResults({
  page,
  query,
  searching,
}: {
  page: ContractPage | null;
  query: string;
  searching: boolean;
}) {
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [direction, setDirection] = useState<SortDirection>('asc');
  const clock = useClock();

  const sorted = useMemo(
    () =>
      page && sortKey
        ? sortContracts(page.contracts, sortKey, direction, clock.today())
        : (page?.contracts ?? []),
    [page, sortKey, direction, clock],
  );
  const totalPages = pageCount(sorted.length, pageSize);
  const visible = pageSlice(sorted, pageNumber, pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setDirection('asc');
    }
    setPageNumber(1);
  };

  const noResults = page === null || page.contracts.length === 0;
  const placeholder = searching ? (
    <div className="intl-text-body-small py-12 text-center text-muted-foreground">
      Searching…
    </div>
  ) : noResults ? (
    <EmptyState
      heading={
        page === null ? (
          'Search to get started'
        ) : (
          <>
            No match found for <span className="font-bold">{query}</span>
          </>
        )
      }
      body={
        page === null
          ? 'Enter a contract id, VIN, policy number or dealer account in the search bar above and press Search to see results.'
          : 'Enter a different contract id, VIN, policy number or dealer account in the search bar above and press "Search" to see results.'
      }
    />
  ) : undefined;

  return (
    <>
      {page && !searching ? <ResultsSummary page={page} query={query} /> : null}

      <ContractResultsTable
        contracts={visible}
        placeholder={placeholder}
        activeSortKey={sortKey}
        direction={direction}
        onSort={handleSort}
      />

      {!noResults && !searching ? (
        <ResultsPagination
          page={pageNumber}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setPageNumber}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      ) : null}
    </>
  );
}

export function ContractSearchPage() {
  const { query, results } = useLoaderData() as SearchLoaderData;
  const [, setSearchParams] = useSearchParams();
  const searching = useNavigation().state === 'loading';
  const [inputValue, setInputValue] = useState(query);

  useEffect(() => {
    setInputValue(query);
  }, [query]);

  const submit = () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed });
    }
  };

  return (
    <div className="flex min-h-full flex-col gap-4 bg-white py-4">
      <div className="flex flex-col gap-1 px-6">
        <h1 className="intl-headline-h2-medium text-foreground">
          CONTRACT SEARCH
        </h1>
        <p className="intl-text-body-small intl-text-tertiary">
          Search by VIN, Contract # or Dealer
        </p>
      </div>

      <SearchForm
        value={inputValue}
        onValueChange={setInputValue}
        onSubmit={submit}
        onClear={() => {
          setInputValue('');
          setSearchParams({});
        }}
      />

      {results && !results.ok ? (
        <div className="mx-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700">
          {results.message}
        </div>
      ) : (
        <SearchResults
          key={query}
          page={results?.ok ? results.value : null}
          query={query}
          searching={searching}
        />
      )}
    </div>
  );
}
